import React, { useState } from 'react';
import { arrayOf, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { SecondaryButton } from '../Button/Button';
import BillingDetailsSummary from '../BillingDetailsSummary/BillingDetailsSummary';
import BillingDetailsForm from '../../forms/BillingDetailsForm/BillingDetailsForm';

import css from './VatExplanation.css';

// Who sells the customer what on each payment path (dynamic VAT decision 1:
// by card the freelancer sells the work, on the invoice and light paths
// Freedomly does; the service fee is always Freedomly's own).
const SELLERS_MESSAGE = {
  stripe: 'VatExplanation.sellers.stripe',
  invoice: 'VatExplanation.sellers.invoice',
  'light-main': 'VatExplanation.sellers.light',
  'light-card': 'VatExplanation.sellers.light',
};

// The billing details a confirmed purchase is priced from, and the one
// sentence on the VAT basis (offer accept confirm step spec §4). Rendered
// only with an ok preview; the amounts sit next to it in the host.
const VatExplanation = props => {
  const {
    className,
    billing,
    buyerValidation,
    path,
    isCardPayment,
    lockedFields,
    formId,
    onSaveBillingDetails,
    saveBillingDetailsInProgress,
    saveBillingDetailsError,
    onBillingSaved,
  } = props;
  const [editOpen, setEditOpen] = useState(false);
  const sellersMessage = SELLERS_MESSAGE[path] || null;

  return (
    <section className={classNames(css.root, className)}>
      <h3 className={css.title}>
        <FormattedMessage id="VatExplanation.title" />
      </h3>
      <BillingDetailsSummary billing={billing} vatValidation={buyerValidation} />
      {buyerValidation === 'UNVERIFIED' ? (
        <p className={css.warning}>
          <FormattedMessage id="VatPreview.unverifiedVat" />
        </p>
      ) : null}
      <SecondaryButton
        type="button"
        className={css.editButton}
        onClick={() => setEditOpen(!editOpen)}
      >
        <FormattedMessage id="VatPreview.editBilling" />
      </SecondaryButton>
      {editOpen ? (
        <BillingDetailsForm
          compact
          formId={formId}
          initialValues={billing}
          lockedFields={lockedFields}
          inProgress={saveBillingDetailsInProgress}
          saveError={saveBillingDetailsError}
          onSubmit={record =>
            onSaveBillingDetails(record)
              .then(() => {
                setEditOpen(false);
                onBillingSaved();
              })
              .catch(() => null)
          }
        />
      ) : null}
      {sellersMessage ? (
        <p className={css.text}>
          <FormattedMessage id={sellersMessage} />
        </p>
      ) : null}
      <p className={css.text}>
        <FormattedMessage
          id={isCardPayment ? 'VatExplanation.basisCard' : 'VatExplanation.basis'}
        />
      </p>
    </section>
  );
};

VatExplanation.defaultProps = {
  className: null,
  buyerValidation: null,
  path: null,
  isCardPayment: false,
  lockedFields: [],
  saveBillingDetailsInProgress: false,
  saveBillingDetailsError: null,
  onBillingSaved: () => null,
};

VatExplanation.propTypes = {
  className: string,
  billing: object.isRequired,
  buyerValidation: string,
  path: string,
  isCardPayment: bool,
  lockedFields: arrayOf(string),
  formId: string.isRequired,
  onSaveBillingDetails: func.isRequired,
  saveBillingDetailsInProgress: bool,
  saveBillingDetailsError: propTypes.error,
  onBillingSaved: func,
};

export default VatExplanation;
